import { useAction, useAtom } from '@reatom/npm-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Skeleton, useHapticFeedback } from '@blade/ui-kit'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import {
  balanceVisibleAtom,
  toggleVisibility,
  useVisibilityFormatter,
} from '@root/entities/preferences'
import { AmountFormatted } from '@root/shared/core/format'
import { EyeClosedIcon, EyeIcon, mediaMinTablet } from '@root/shared/ui-kit'

import type { Error } from '@root/shared'
import type BigNumber from 'bignumber.js'

type Totals = {
  native: BigNumber
  usd: BigNumber
}

export type SummaryTotalProps = {
  state: AsyncStateN.AsyncStateN<Error.UnknownError, Totals>
}

export const SummaryTotal: React.FC<SummaryTotalProps> = ({ state }) => {
  const { t } = useTranslation()
  const toggle = useAction(toggleVisibility)
  const [isVisible] = useAtom(balanceVisibleAtom)
  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)
  const formatVisibility = useVisibilityFormatter('****')
  const haptics = useHapticFeedback()

  const VisibilityIcon = isVisible ? EyeIcon : EyeClosedIcon

  const renderBalance = () => {
    // TODO: Error styles
    if (!AsyncStateN.isSuccess(state)) {
      return <Skeleton height={30} width={160} />
    }

    return (
      <>
        {formatVisibility(
          <AmountFormatted amount={state.value.native} roundingMode="trunc" />,
        )}
        <span> {nativeCurrency.ticker}</span>
      </>
    )
  }

  const renderAlternate = () => {
    // TODO: Error styles
    if (!AsyncStateN.isSuccess(state)) {
      return <Skeleton height={18} width={70} />
    }

    return (
      <>
        {formatVisibility(
          <AmountFormatted
            amount={state.value.usd}
            currency="$"
            roundingMode="trunc"
          />,
        )}
      </>
    )
  }

  return (
    <Root>
      <TitleContainer>
        <Title>{t('summary.your_summary')}</Title>
        <VisibilityIconWrapper>
          <VisibilityIcon
            onClick={(e) => {
              e.stopPropagation()
              haptics.impactOccurred('light')
              toggle()
            }}
            size={16}
          />
        </VisibilityIconWrapper>
      </TitleContainer>

      <Amount>{renderBalance()}</Amount>
      <AmountAlternate>{renderAlternate()}</AmountAlternate>
    </Root>
  )
}

const Root = styled.div``

const TitleContainer = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`
const VisibilityIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
`

const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  opacity: 0.5;

  ${mediaMinTablet(css`
    font-size: 14px;
  `)}
`

const Amount = styled.div`
  font-size: 30px;
  font-weight: 700;
  margin-block: 4px 6px;
  white-space: nowrap;
`

const AmountAlternate = styled.div`
  font-size: 14px;
  font-weight: 700;
`
